import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSend } from 'react-icons/fi';
import {
    submitMessage,
    streamMessage,
    addMessagesToChat,
    createChat,
    getChatById
} from '../../services/adamConciergeService';
import hotelService from '../../services/hotelService';

const AdamChat = ({
    onHotelsFound,
    onExperiencesFound,
    onNewChat,
    activeChatId,
    initialMessages,
    agentId,
    agentName,
    sidebarOpen = false,
    onToggleSidebar,
    onChatTitleUpdate,
    fallbackSearchParams = null
}) => {
    const [userMessage, setUserMessage] = useState('');
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const messageContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const [currentChatId, setCurrentChatId] = useState(activeChatId);
    const pendingMessagesRef = useRef([]);
    const [sentMsgBlock, setSentMsgBlock] = useState(false);
    const navigate = useNavigate();
    const [hotelsEmissions, setHotelsEmissions] = useState({});
    const [loadingEmissions, setLoadingEmissions] = useState({});

    const formatTimestamp = useCallback((date) => {
        const options = { hour: '2-digit', minute: '2-digit' };
        return date.toLocaleTimeString('en-US', options).replace(/:/, '.');
    }, []);

    // Filter hotels to only include those with images and prices; for single-hotel (e.g. hotel details response) show card even without image/price
    const filterValidHotels = useCallback((hotels) => {
        if (!hotels || !Array.isArray(hotels)) return [];
        const withImageAndPrice = hotels.filter(hotel => {
            const hasImage = hotel.imageUrl && hotel.imageUrl.trim() !== '';
            const hasPrice = hotel.lowestRate !== null && hotel.lowestRate !== undefined && hotel.lowestRate > 0;
            return hasImage && hasPrice;
        });
        if (withImageAndPrice.length > 0) return withImageAndPrice;
        // Single hotel from hotel-details response (object with hotelCode/hotelName but no image/price) – still show card
        if (hotels.length === 1 && hotels[0] && (hotels[0].hotelCode != null || hotels[0].hotelName)) {
            return [hotels[0]];
        }
        return [];
    }, []);

    const getCarbonTierColor = (tierName) => {
        if (!tierName || typeof tierName !== 'string') return 'bg-gray-200 text-gray-700';
        const tier = tierName.toLowerCase();
        if (tier.includes('low') || tier.includes('green')) return 'bg-green-100 text-green-800';
        if (tier.includes('medium') || tier.includes('moderate')) return 'bg-yellow-100 text-yellow-800';
        if (tier.includes('high') || tier.includes('impact')) return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    };

    const formatEmissions = (value) => {
        if (value === undefined || value === null) return 'N/A';
        if (value < 1) return value.toFixed(3);
        if (value < 10) return value.toFixed(2);
        if (value < 100) return value.toFixed(1);
        return Math.round(value);
    };

    const fetchHotelEmissionsForChat = useCallback(async (hotel, hotelKey) => {
        const countryName = hotel.countryName || hotel.country || '';
        const cityName = hotel.city || '';
        if (!countryName && !cityName) return;
        setLoadingEmissions(prev => ({ ...prev, [hotelKey]: true }));
        try {
            const emissionsData = await hotelService.previewEmissions({
                hotelLocation: { country: countryName, state: cityName, countryCode: hotel.countryCode || '' },
                amenitiesList: [],
                ecoCertified: false,
                occupancy: 1,
                roomType: 'standard',
                selectedRoomType: null,
                numberOfNights: 1,
                numberOfRooms: 1,
            });
            setHotelsEmissions(prev => ({ ...prev, [hotelKey]: emissionsData }));
        } catch {
            setHotelsEmissions(prev => ({ ...prev, [hotelKey]: null }));
        } finally {
            setLoadingEmissions(prev => ({ ...prev, [hotelKey]: false }));
        }
    }, []);

    useEffect(() => {
        if (!userMessage && textareaRef.current) {
            const ta = textareaRef.current;
            ta.style.height = '';
            ta.style.overflowY = 'hidden';
        }
    }, [userMessage]);

    useEffect(() => {
        const allHotels = [];
        messages.forEach((msg, msgIdx) => {
            if ((msg.type === 'hotels' || msg.hotels) && msg.hotels?.length > 0) {
                msg.hotels.forEach((h, hIdx) => {
                    const key = h.hotelCode || h.hotelcode || `msg${msgIdx}_${hIdx}`;
                    if (!hotelsEmissions[key] && !loadingEmissions[key] && (h.city || h.countryName)) {
                        allHotels.push({ hotel: h, key });
                    }
                });
            }
        });
        allHotels.forEach(({ hotel, key }) => fetchHotelEmissionsForChat(hotel, key));
    }, [messages, fetchHotelEmissionsForChat]);

    // Clean markdown formatting from messages
    const cleanMarkdown = useCallback((text) => {
        if (!text || typeof text !== 'string') return text;
        
        // Remove markdown headers (# ## ### etc.)
        let cleaned = text.replace(/^#{1,6}\s+/gm, '');
        
        // Remove markdown list markers at start of lines (- * +)
        cleaned = cleaned.replace(/^[-*+]\s+/gm, '');
        
        // Convert **bold** to plain text (remove the **)
        cleaned = cleaned.replace(/\*\*([^*]+)\*\*/g, '$1');
        
        // Convert *italic* to plain text
        cleaned = cleaned.replace(/\*([^*]+)\*/g, '$1');
        
        // Remove markdown links [text](url) -> text
        cleaned = cleaned.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');
        
        return cleaned;
    }, []);

    // Render bold text (convert **text** to <strong>)
    const renderBoldText = useCallback((text) => {
        if (!text || typeof text !== 'string') return '';
        const parts = text.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={index} className="font-medium">{part.slice(2, -2)}</strong>;
            }
            return part;
        });
    }, []);

    // Render message with line breaks and bold text
    const renderMessageWithBreaks = useCallback((message) => {
        if (!message || typeof message !== 'string') {
            return <span>No message content</span>;
        }
        // First clean markdown headers and lists
        const cleaned = cleanMarkdown(message);
        // Split by newlines
        const lines = cleaned.split('\n');
        return lines.map((line, index) => (
            <span key={index}>
                {renderBoldText(line)}
                {index < lines.length - 1 && <br />}
            </span>
        ));
    }, [cleanMarkdown, renderBoldText]);

    // Sync chat ID
    useEffect(() => {
        setCurrentChatId(activeChatId);
    }, [activeChatId]);

    // Load initial messages
    useEffect(() => {
        if (initialMessages !== null && initialMessages !== undefined) {
            if (Array.isArray(initialMessages) && initialMessages.length > 0) {
                const formattedMessages = initialMessages.map(msg => {
                    const validHotels = msg.hotels ? filterValidHotels(msg.hotels) : [];
                    return {
                        sender: msg.sender === 'user' ? (agentName || 'Agent') : 'Seren',
                        message: msg.message || '',
                        timestamp: msg.timestamp
                            ? new Date(msg.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }).replace(/:/, '.')
                            : formatTimestamp(new Date()),
                        type: msg.type || 'text',
                        hotels: validHotels,
                        experiences: msg.experiences || [],
                        tripPlan: msg.tripPlan || null,
                        label: msg.label,
                        searchParams: msg.searchParams || null
                    };
                });
                setMessages(formattedMessages);
            } else {
                setMessages([{
                    sender: 'Seren',
                    message: `Hello ${agentName || 'there'}! I'm Seren, your AI travel planning assistant. I can help you search for hotels, plan trips for your clients, and discover curated experiences. How can I assist you today?`,
                    timestamp: formatTimestamp(new Date()),
                    type: 'text'
                }]);
            }
        }
    }, [initialMessages, agentName, formatTimestamp]);

    // Save messages to DB
    const saveMessagesToDB = useCallback(async (messagesToSave) => {
        if (!agentId || !currentChatId) return;
        try {
            const dbMessages = messagesToSave.map(msg => {
                const validHotels = msg.hotels ? filterValidHotels(msg.hotels) : [];
                return {
                    sender: msg.sender === 'Seren' ? 'Seren' : 'user',
                    message: msg.message || '',
                    timestamp: new Date(),
                    type: msg.type || 'text',
                    hotels: validHotels,
                    experiences: msg.experiences || [],
                    tripPlan: msg.tripPlan || null,
                    label: msg.label,
                    searchParams: msg.searchParams || null
                };
            });

            const result = await addMessagesToChat(currentChatId, agentId, dbMessages);
            if (result.success && result.data?.title && onChatTitleUpdate) {
                onChatTitleUpdate(currentChatId, result.data.title);
            }
        } catch (error) {
            console.error('Error saving messages:', error);
        }
    }, [agentId, currentChatId, onChatTitleUpdate]);

    // Auto-scroll
    useEffect(() => {
        if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
        }
    }, [messages, isTyping]);

    // Ensure chat exists
    const ensureChatExists = useCallback(async () => {
        if (currentChatId) return currentChatId;
        if (!agentId) return null;

        try {
            const response = await createChat();
            if (response.success) {
                const newChatId = response.data.chatId;
                setCurrentChatId(newChatId);
                if (onNewChat) onNewChat(newChatId, response.data);
                return newChatId;
            }
        } catch (error) {
            console.error('Error creating chat:', error);
        }
        return null;
    }, [currentChatId, agentId, onNewChat]);

    // Send message
    const handleSendMessage = useCallback(async () => {
        if (!userMessage.trim() || isLoading || sentMsgBlock) return;

        const trimmedMessage = userMessage.trim();
        setUserMessage('');
        setSentMsgBlock(true);
        setIsLoading(true);

        // Ensure chat exists
        const chatId = await ensureChatExists();

        // Add user message to UI
        const userMsg = {
            sender: agentName || 'Agent',
            message: trimmedMessage,
            timestamp: formatTimestamp(new Date()),
            type: 'text'
        };
        setMessages(prev => [...prev, userMsg]);

        // Show typing indicator
        setIsTyping(true);

        try {
            // Use streaming
            let responseReceived = false;

            await streamMessage(
                trimmedMessage,
                chatId,
                // onMessage
                (data) => {
                    if (data.type === 'response') {
                        responseReceived = true;
                        setIsTyping(false);

                        const messagesToAdd = [];

                        // Text response
                        if (data.data) {
                            const adamMsg = {
                                sender: 'Seren',
                                message: data.data,
                                timestamp: formatTimestamp(new Date()),
                                type: 'text',
                                tripPlan: data.tripPlan || null
                            };
                            messagesToAdd.push(adamMsg);
                        }

                        // Experience results first (Curated Experiences before Recommended Hotels)
                        if (data.experienceCodes && data.experienceCodes.length > 0) {
                            const expMsg = {
                                sender: 'Serene',
                                message: '',
                                timestamp: formatTimestamp(new Date()),
                                type: 'experiences',
                                experiences: data.experienceCodes,
                                label: 'Curated Experiences'
                            };
                            messagesToAdd.push(expMsg);
                            if (onExperiencesFound) onExperiencesFound(data.experienceCodes);
                        }

                        // Hotel results - show after experiences (separate message when we have both so order is: text, experiences, hotels)
                        if (data.hotelCodes && data.hotelCodes.length > 0) {
                            const validHotels = filterValidHotels(data.hotelCodes);
                            if (validHotels.length > 0) {
                                const searchParams = data.intent?.entities ? {
                                    checkInDate: data.intent.entities.checkInDate || null,
                                    checkOutDate: data.intent.entities.checkOutDate || null,
                                    adults: data.intent.entities.adults || 2,
                                    children: data.intent.entities.children || 0,
                                    currency: 'USD'
                                } : null;
                                const hasExperiences = data.experienceCodes && data.experienceCodes.length > 0;
                                const hotelMsg = {
                                    sender: 'Seren',
                                    message: '',
                                    timestamp: formatTimestamp(new Date()),
                                    type: 'hotels',
                                    hotels: validHotels,
                                    label: data.tripPlan ? 'Recommended Hotels' : 'Hotel Suggestions',
                                    searchParams: searchParams
                                };
                                if (data.tripPlan && !hasExperiences) {
                                    const textMsg = messagesToAdd.find(m => m.type === 'text');
                                    if (textMsg) {
                                        textMsg.hotels = validHotels;
                                        textMsg.searchParams = searchParams;
                                    } else {
                                        messagesToAdd.push(hotelMsg);
                                    }
                                } else {
                                    messagesToAdd.push(hotelMsg);
                                }
                                if (onHotelsFound) onHotelsFound(validHotels, searchParams);
                            }
                        }

                        setMessages(prev => [...prev, ...messagesToAdd]);

                        // Save to DB
                        if (chatId) {
                            saveMessagesToDB([userMsg, ...messagesToAdd]);
                        }
                    }
                },
                // onError
                (error) => {
                    setIsTyping(false);
                    if (!responseReceived) {
                        const errMsg = {
                            sender: 'Serene',
                            message: "I'm sorry, I encountered an issue. Please try again.",
                            timestamp: formatTimestamp(new Date()),
                            type: 'text'
                        };
                        setMessages(prev => [...prev, errMsg]);
                    }
                },
                // onComplete
                () => {
                    setIsTyping(false);
                    setIsLoading(false);
                    setSentMsgBlock(false);
                }
            );
        } catch (error) {
            console.error('Error sending message:', error);
            setIsTyping(false);
            setIsLoading(false);
            setSentMsgBlock(false);

            // Fallback to non-streaming
            try {
                const result = await submitMessage(trimmedMessage, chatId);
                if (result.success && result.data) {
                    const messagesToAdd = [];

                    if (result.data.responseData?.data) {
                        const validHotels = result.data.hotelCodes ? filterValidHotels(result.data.hotelCodes) : null;
                        messagesToAdd.push({
                            sender: 'Seren',
                            message: result.data.responseData.data,
                            timestamp: formatTimestamp(new Date()),
                            type: 'text',
                            tripPlan: result.data.tripPlan || null,
                            hotels: validHotels && validHotels.length > 0 ? validHotels : null,
                            searchParams: result.data.intent?.entities ? {
                                checkInDate: result.data.intent.entities.checkInDate || null,
                                checkOutDate: result.data.intent.entities.checkOutDate || null,
                                adults: result.data.intent.entities.adults || 2,
                                children: result.data.intent.entities.children || 0,
                                currency: 'USD'
                            } : null
                        });
                    }

                    // Curated Experiences first, then Recommended Hotels
                    if (result.data.experienceCodes?.length > 0) {
                        messagesToAdd.push({
                            sender: 'Serene',
                            message: '',
                            timestamp: formatTimestamp(new Date()),
                            type: 'experiences',
                            experiences: result.data.experienceCodes,
                            label: 'Curated Experiences'
                        });
                        if (onExperiencesFound) onExperiencesFound(result.data.experienceCodes);
                    }

                    if (result.data.hotelCodes?.length > 0) {
                        const validHotels = filterValidHotels(result.data.hotelCodes);
                        if (validHotels.length > 0) {
                            const searchParams = result.data.intent?.entities ? {
                                checkInDate: result.data.intent.entities.checkInDate || null,
                                checkOutDate: result.data.intent.entities.checkOutDate || null,
                                adults: result.data.intent.entities.adults || 2,
                                children: result.data.intent.entities.children || 0,
                                currency: 'USD'
                            } : null;
                            const hasExperiences = result.data.experienceCodes?.length > 0;
                            const hotelMsg = {
                                sender: 'Seren',
                                message: '',
                                timestamp: formatTimestamp(new Date()),
                                type: 'hotels',
                                hotels: validHotels,
                                label: result.data.tripPlan ? 'Recommended Hotels' : 'Hotel Suggestions',
                                searchParams: searchParams
                            };
                            if (result.data.tripPlan && !hasExperiences) {
                                const existingTextMsg = messagesToAdd.find(m => m.type === 'text' && m.tripPlan);
                                if (existingTextMsg) {
                                    existingTextMsg.hotels = validHotels;
                                    existingTextMsg.searchParams = searchParams;
                                } else {
                                    messagesToAdd.push(hotelMsg);
                                }
                            } else {
                                messagesToAdd.push(hotelMsg);
                            }
                            if (onHotelsFound) onHotelsFound(validHotels, searchParams);
                        }
                    }

                    setMessages(prev => [...prev, ...messagesToAdd]);

                    if (chatId) {
                        saveMessagesToDB([userMsg, ...messagesToAdd]);
                    }
                }
            } catch (fallbackError) {
                const errMsg = {
                    sender: 'Adam',
                    message: "I'm sorry, I'm unable to connect right now. Please try again later.",
                    timestamp: formatTimestamp(new Date()),
                    type: 'text'
                };
                setMessages(prev => [...prev, errMsg]);
            }
        }
    }, [userMessage, isLoading, sentMsgBlock, agentName, formatTimestamp, ensureChatExists, onHotelsFound, onExperiencesFound, saveMessagesToDB]);

    // Handle enter key
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Render a single message
    const renderMessage = (msg, index) => {
        const isUser = msg.sender !== 'Seren';

        // Hotel cards
        if (msg.type === 'hotels' && msg.hotels?.length > 0) {
            return (
                <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 px-3 sm:px-4 min-w-0"
                >
                    {msg.label && (
                        <div className="flex items-center gap-2 mb-3 min-w-0">
                            <div className="w-1 h-1 rounded-full bg-black shrink-0" />
                            <span className="text-xs font-medium tracking-widest uppercase text-gray-500 break-words">{msg.label}</span>
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                         {msg.hotels.slice(0, 6).map((hotel, hIdx) => {
                             // Use message searchParams first, then fallback (e.g. from parent) so links keep check-in/out after refresh
                             const checkInDate = msg.searchParams?.checkInDate ?? fallbackSearchParams?.checkInDate ?? null;
                             const checkOutDate = msg.searchParams?.checkOutDate ?? fallbackSearchParams?.checkOutDate ?? null;
                             const adults = msg.searchParams?.adults ?? fallbackSearchParams?.adults ?? 2;
                             const children = msg.searchParams?.children ?? fallbackSearchParams?.children ?? 0;
                             
                             const hotelCode = hotel.hotelcode || hotel.hotelCode || hotel.sabreHotelCode;
                             
                             // Build URL params - include check-in/out when available so refresh keeps dates
                             const urlParams = new URLSearchParams();
                             if (checkInDate) urlParams.set('checkInDate', checkInDate);
                             if (checkOutDate) urlParams.set('checkOutDate', checkOutDate);
                             urlParams.set('adults', adults.toString());
                             urlParams.set('children', children.toString());
                             urlParams.set('currency', 'USD');
                             
                             const hotelUrl = hotelCode ? `/hotel/${hotelCode}?${urlParams.toString()}` : '#';
                            
                            const hotelSearchParams = {
                                checkInDate,
                                checkOutDate,
                                adults,
                                children,
                                currency: 'USD'
                            };
                            return (
                            <div
                                key={hIdx}
                                className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group"
                            >
                                <div
                                    className="cursor-pointer"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (hotelCode) window.open(hotelUrl, '_blank', 'noopener,noreferrer');
                                    }}
                                >
                                <div className="h-32 overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300">
                                    {hotel.imageUrl ? (
                                        <img
                                            src={hotel.imageUrl}
                                            alt={hotel.hotelName}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            loading="lazy"
                                            onError={(e) => { 
                                                e.target.onerror = null; 
                                                e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=300&fit=crop&q=80';
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <div className="p-3">
                                    <h4 className="text-sm font-medium text-gray-900 truncate">{hotel.hotelName}</h4>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {hotel.city}{hotel.countryName ? `, ${hotel.countryName}` : ''}
                                    </p>
                                    {hotel.lowestRate && (
                                        <p className="text-xs font-medium text-gray-900 mt-1">
                                            From USD {hotel.lowestRate}/night
                                        </p>
                                    )}
                                    {(() => {
                                        const hotelKey = hotel.hotelCode || hotel.hotelcode || `msg${index}_${hIdx}`;
                                        const emissionsData = hotelsEmissions[hotelKey];
                                        const isLoading = loadingEmissions[hotelKey];
                                        if (isLoading) {
                                            return <div className="mt-2 w-20 h-4 bg-gray-100 rounded animate-pulse" />;
                                        }
                                        if (emissionsData?.data) {
                                            const emissions = emissionsData.data;
                                            const tierName = typeof (emissions.carbonTier?.tierName ?? emissions.tierName) === 'string' ? (emissions.carbonTier?.tierName ?? emissions.tierName) : null;
                                            const emissionValue = emissions.carbonEmission ?? null;
                                            if (!tierName && emissionValue == null) return null;
                                            return (
                                                <div className="mt-2 pt-2 border-t border-gray-100 space-y-0.5">
                                                    {tierName && (
                                                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${getCarbonTierColor(tierName)}`}>
                                                            {tierName}
                                                        </span>
                                                    )}
                                                    {emissionValue != null && (
                                                        <p className="text-[10px] text-gray-600">
                                                            <span className="font-semibold">{formatEmissions(emissionValue)}</span> kg CO₂e
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                </motion.div>
            );
        }

        // Experience cards
        if (msg.type === 'experiences' && msg.experiences?.length > 0) {
            return (
                <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 px-3 sm:px-4 min-w-0"
                >
                    {msg.label && (
                        <div className="flex items-center gap-2 mb-3 min-w-0">
                            <div className="w-1 h-1 rounded-full bg-black shrink-0" />
                            <span className="text-xs font-medium tracking-widest uppercase text-gray-500 break-words">{msg.label}</span>
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                        {msg.experiences.slice(0, 6).map((exp, eIdx) => {
                            const videoUrl = (exp.videos?.verticalVideo || exp.videos?.coverVideo || '').trim();
                            const expId = exp.id || exp._id;
                            return (
                            <div
                                key={eIdx}
                                role="button"
                                tabIndex={0}
                                onClick={() => { if (expId) window.open(`/experience/${expId}`, '_blank', 'noopener,noreferrer'); }}
                                onKeyDown={(e) => { if (expId && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); window.open(`/experience/${expId}`, '_blank', 'noopener,noreferrer'); } }}
                                className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                            >
                                {(videoUrl || exp.coverImage) && (
                                    <div className="h-40 overflow-hidden bg-gray-100">
                                        {videoUrl ? (
                                            <video
                                                src={videoUrl}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                autoPlay
                                                muted
                                                loop
                                                playsInline
                                                preload="auto"
                                            />
                                        ) : (
                                            <img
                                                src={exp.coverImage}
                                                alt={exp.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        )}
                                    </div>
                                )}
                                <div className="p-3">
                                    <h4 className="text-sm font-medium text-gray-900 truncate">{exp.title}</h4>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {exp.location?.city}{exp.location?.country ? `, ${exp.location.country}` : ''}
                                    </p>
                                    <div className="flex items-center justify-between mt-1">
                                        {(exp.price != null || exp.listingTotal != null) && (
                                            <span className="text-xs font-medium text-gray-900">
                                                {exp.currency === 'GBP' ? '£' : exp.currency === 'USD' ? '$' : '€'}
                                                {(exp.listingTotal ?? exp.price ?? ((exp.basePrice || 0) + (exp.commissionAmount || 0))).toLocaleString()}
                                            </span>
                                        )}
                                        {exp.category && (
                                            <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
                                                {exp.category}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                </motion.div>
            );
        }

        // Text message with optional hotel cards (for trip plans)
        return (
            <div key={index} className="mb-4">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3 px-3 sm:px-4 min-w-0`}
                >
                    <div className={`max-w-[min(92%,28rem)] sm:max-w-[80%] min-w-0 ${isUser ? 'order-2' : 'order-1'}`}>
                        {/* Sender name */}
                        <div className={`text-[10px] uppercase tracking-wider mb-1 ${isUser ? 'text-right text-gray-400' : 'text-gray-400'}`}>
                            {isUser ? 'You' : 'Seren'}
                        </div>

                        {/* Message bubble */}
                        <div className={`rounded-2xl px-3 sm:px-4 py-3 min-w-0 ${isUser
                            ? 'bg-black text-white rounded-br-sm'
                            : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                            }`}>
                            <div className="text-sm leading-relaxed whitespace-pre-wrap break-words hyphens-auto">
                                {renderMessageWithBreaks(msg.message)}
                            </div>
                        </div>

                        {/* Timestamp */}
                        <div className={`text-[9px] text-gray-400 mt-1 ${isUser ? 'text-right' : 'text-left'}`}>
                            {msg.timestamp}
                        </div>
                    </div>
                </motion.div>

                {/* Hotel cards below text message (for trip plans) */}
                {!isUser && msg.hotels && msg.hotels.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.4 }}
                        className="px-3 sm:px-4 mb-4 min-w-0"
                    >
                        <div className="flex items-center gap-2 mb-3 min-w-0">
                            <div className="w-1 h-1 rounded-full bg-black shrink-0" />
                            <span className="text-xs font-medium tracking-widest uppercase text-gray-500 break-words">
                                {msg.tripPlan ? 'Recommended Hotels' : 'Hotel Suggestions'}
                            </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 min-w-0">
                            {msg.hotels.slice(0, 6).map((hotel, hIdx) => {
                                const checkInDate = msg.searchParams?.checkInDate ?? fallbackSearchParams?.checkInDate ?? null;
                                const checkOutDate = msg.searchParams?.checkOutDate ?? fallbackSearchParams?.checkOutDate ?? null;
                                const adults = msg.searchParams?.adults ?? fallbackSearchParams?.adults ?? 2;
                                const children = msg.searchParams?.children ?? fallbackSearchParams?.children ?? 0;
                                
                                const hotelCode = hotel.hotelcode || hotel.hotelCode || hotel.sabreHotelCode;
                                
                                const urlParams = new URLSearchParams();
                                if (checkInDate) urlParams.set('checkInDate', checkInDate);
                                if (checkOutDate) urlParams.set('checkOutDate', checkOutDate);
                                urlParams.set('adults', adults.toString());
                                urlParams.set('children', children.toString());
                                urlParams.set('currency', 'USD');
                                
                                const hotelUrl = hotelCode ? `/hotel/${hotelCode}?${urlParams.toString()}` : '#';
                                
                                return (
                                    <div
                                        key={hIdx}
                                        className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (hotelCode) {
                                                window.open(hotelUrl, '_blank', 'noopener,noreferrer');
                                            }
                                        }}
                                    >
                                        <div className="h-32 overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300">
                                            {hotel.imageUrl ? (
                                                <img
                                                    src={hotel.imageUrl}
                                                    alt={hotel.hotelName}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                    loading="lazy"
                                                    onError={(e) => { 
                                                        e.target.onerror = null; 
                                                        e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=300&fit=crop&q=80';
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-3">
                                            {/* Name + Category badge (match main hotel list styling) */}
                                            <div className="flex items-start justify-between gap-2">
                                                <h4 className="text-sm font-medium text-gray-900 truncate">
                                                    {hotel.hotelName}
                                                </h4>
                                                {hotel.category && hotel.category !== 'No Chain Code' && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-900 text-white">
                                                        {hotel.category === 'Recommended' && (
                                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                            </svg>
                                                        )}
                                                        {hotel.category === 'Compass' && (
                                                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                                            </svg>
                                                        )}
                                                        {hotel.category}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {hotel.city}{hotel.countryName ? `, ${hotel.countryName}` : ''}
                                            </p>
                                            {hotel.lowestRate && (
                                                <p className="text-xs font-medium text-gray-900 mt-1">
                                                    From USD {hotel.lowestRate}/night
                                                </p>
                                            )}
                                            {(() => {
                                                const hotelKey = hotel.hotelCode || hotel.hotelcode || `msg${index}_${hIdx}`;
                                                const emissionsData = hotelsEmissions[hotelKey];
                                                const isLoading = loadingEmissions[hotelKey];
                                                if (isLoading) return <div className="mt-2 w-20 h-4 bg-gray-100 rounded animate-pulse" />;
                                                if (emissionsData?.data) {
                                                    const emissions = emissionsData.data;
                                                    const tierName = typeof (emissions.carbonTier?.tierName ?? emissions.tierName) === 'string' ? (emissions.carbonTier?.tierName ?? emissions.tierName) : null;
                                                    const emissionValue = emissions.carbonEmission ?? null;
                                                    if (!tierName && emissionValue == null) return null;
                                                    return (
                                                        <div className="mt-2 pt-2 border-t border-gray-100 space-y-0.5">
                                                            {tierName && (
                                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${getCarbonTierColor(tierName)}`}>
                                                                    {tierName}
                                                                </span>
                                                            )}
                                                            {emissionValue != null && (
                                                                <p className="text-[10px] text-gray-600">
                                                                    <span className="font-semibold">{formatEmissions(emissionValue)}</span> kg CO₂e
                                                                </p>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full min-h-0 w-full min-w-0 overflow-hidden">
            {/* Chat header */}
            <div className="flex-none px-3 sm:px-4 py-2 border-b border-gray-100 flex items-center gap-2 sm:gap-3 min-w-0">
                <button
                    onClick={onToggleSidebar}
                    className="p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-500 shrink-0"
                    title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sidebarOpen ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                        )}
                    </svg>
                </button>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse shrink-0" />
                    <span className="text-[10px] sm:text-xs text-gray-500 font-medium tracking-wider uppercase">
                        Seren is online
                    </span>
                </div>
            </div>

            {/* Messages */}
            <div
                ref={messageContainerRef}
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-3 sm:py-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
            >
                <AnimatePresence>
                    {messages.map((msg, index) => renderMessage(msg, index))}
                </AnimatePresence>

                {/* Typing indicator */}
                {isTyping && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="flex justify-start mb-3 px-3 sm:px-4 min-w-0"
                    >
                        <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                                <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Input area */}
            <div className="flex-none border-t border-gray-100 bg-white p-3 md:p-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                <div className="flex items-end gap-2 max-w-4xl mx-auto min-w-0">
                    <div className="flex-1 relative min-w-0">
                        <textarea
                            ref={textareaRef}
                            value={userMessage}
                            onChange={(e) => setUserMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask Seren anything about hotels, trips, or experiences..."
                            className="w-full min-w-0 resize-none border border-gray-200 rounded-xl px-4 py-3.5 pr-14 text-[15px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 transition-all bg-gray-50 min-h-[56px] max-h-[220px] box-border whitespace-pre-wrap break-words hyphens-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent [scrollbar-gutter:stable]"
                            rows={1}
                            disabled={isLoading}
                            onInput={(e) => {
                                const ta = e.target;
                                ta.style.height = 'auto';
                                const maxPx = 220;
                                const h = Math.min(ta.scrollHeight, maxPx);
                                ta.style.height = `${h}px`;
                                ta.style.overflowY = ta.scrollHeight > maxPx ? 'auto' : 'hidden';
                            }}
                        />
                    </div>

                    <motion.button
                        onClick={handleSendMessage}
                        disabled={!userMessage.trim() || isLoading}
                        className={`flex-none w-11 h-11 rounded-xl flex items-center justify-center transition-all ${userMessage.trim() && !isLoading
                            ? 'bg-black text-white hover:bg-gray-800 shadow-md'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                        whileHover={userMessage.trim() && !isLoading ? { scale: 1.05 } : {}}
                        whileTap={userMessage.trim() && !isLoading ? { scale: 0.95 } : {}}
                    >
                        {isLoading ? (
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-white rounded-full animate-spin" />
                        ) : (
                            <FiSend className="w-4 h-4" />
                        )}
                    </motion.button>
                </div>

                <div className="text-center mt-2">
                    <span className="text-[9px] text-gray-400 tracking-wider uppercase">
                        Powered by AXONALL AI
                    </span>
                </div>
            </div>
        </div>
    );
};

export default AdamChat;
